import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Result, Button, Typography, Collapse, Space, Spin } from 'antd';
import { ReloadOutlined, BugOutlined, HomeOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
  /**
   * Bumped whenever a recoverable error is swallowed. React has already
   * unmounted the subtree at that point, so the children need a fresh key to be
   * mounted again — without it the app renders blank (the "weisse Seite").
   */
  recoveryKey: number;
  /** Whether the last swallowed error came from translation/extension DOM edits. */
  recoveredFromDomError: boolean;
  /** A stale-build chunk failed to load and the page is reloading itself. */
  reloadingForUpdate: boolean;
  /** The reload already happened and the chunk is still missing. */
  chunkLoadFailed: boolean;
}

// Function to check if an error is from a browser extension
const isExtensionError = (error: Error | ErrorEvent | any): boolean => {
  const errorString = error?.toString?.() || JSON.stringify(error);
  const stack = error?.stack || '';
  const filename = error?.filename || '';
  const message = error?.message || '';
  
  return (
    stack.includes('chrome-extension://') ||
    stack.includes('moz-extension://') ||
    stack.includes('safari-extension://') ||
    filename.includes('chrome-extension://') ||
    filename.includes('moz-extension://') ||
    message.includes('chrome-extension://') ||
    errorString.includes('chrome-extension://') ||
    // Specific error patterns from extensions
    message.includes('Cannot read properties of null') && stack.includes('inpage.js') ||
    message.includes('Cannot read properties of undefined') && stack.includes('inpage.js') ||
    stack.includes('extensions/')
  );
};

// Errors caused by browser auto-translation (e.g. Google Translate) mutating the
// DOM out from under React. React then calls insertBefore/removeChild against a
// node the translator already moved, throwing a NotFoundError. These are
// transient and recoverable — don't show the full-screen crash for them.
const isDomReconciliationError = (error: Error | any): boolean => {
  const name = error?.name || '';
  const message = error?.message || '';

  return (
    name === 'NotFoundError' &&
    (message.includes('insertBefore') ||
      message.includes('removeChild') ||
      message.includes('is not a child of this node'))
  );
};

/**
 * A lazily loaded route chunk could not be fetched.
 *
 * This is what an open tab sees after a deploy: index.html and the chunk names
 * it knows are from the previous build, and those files are gone from the
 * server. The first route the user navigates to that was not loaded yet throws
 * here. It is not a bug in the page they were heading for — reloading picks up
 * the new build and the navigation succeeds.
 */
const isChunkLoadError = (error: Error | any): boolean => {
  const name = error?.name || '';
  const message = error?.message || '';

  return (
    name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
    // Vite/ESM wording, in case a bundler swap ever lands
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module')
  );
};

/** The URL webpack tried to load, taken from the ChunkLoadError it throws. */
const failedChunkUrl = (error: Error | any): string | null => {
  if (typeof error?.request === 'string' && error.request) return error.request;
  const match = /\((?:missing|error): ((?:https?:\/\/|\/)[^)\s]+)\)/.exec(error?.message || '');
  return match ? match[1] : null;
};

/**
 * Overwrite the browser's cached copy of the failed chunk.
 *
 * Chunk files are served `immutable, max-age=1y`. Until 2026-09-02 the SPA
 * fallback answered a missing chunk with index.html *and those headers*, so a
 * browser that hit a deploy at the wrong moment keeps HTML under the chunk URL
 * for a year — and the name only changes when the chunk's content does. A plain
 * reload never re-validates an immutable subresource, so reloading (or Ctrl+F5,
 * which only touches the current page) does not help. `cache: 'reload'` fetches
 * from the network and replaces the cache entry, after which the next
 * `<script>` for that URL gets JavaScript again.
 */
const evictCachedChunk = async (error: Error | any): Promise<void> => {
  const url = failedChunkUrl(error);
  if (!url || typeof fetch !== 'function') return;
  try {
    await fetch(url, { cache: 'reload', credentials: 'same-origin' });
  } catch {
    // Offline or blocked: the reload below will surface that on its own.
  }
};

/** Guards against a reload loop when the chunk is missing for good. */
const CHUNK_RELOAD_KEY = 'herzens_chunk_reload_at';
const CHUNK_RELOAD_COOLDOWN_MS = 30 * 1000;
/** useVersionCheck remembers a dismissed version; a stale tab must forget it. */
const DISMISSED_VERSION_KEY = 'herzens_dismissed_version';

const reloadOnceForNewBuild = (error: Error | any): boolean => {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (Date.now() - last < CHUNK_RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    // Otherwise the "Neue Version verfuegbar" prompt stays suppressed for a
    // version the tab can no longer load anything from.
    localStorage.removeItem(DISMISSED_VERSION_KEY);
  } catch {
    // Private mode / storage disabled: reload anyway, once is better than a
    // crash screen, and without storage there is no loop counter to keep.
  }
  // Evict first: otherwise the reload re-uses the poisoned cache entry and the
  // same chunk fails again straight away.
  void evictCachedChunk(error).finally(() => window.location.reload());
  return true;
};

class ErrorBoundary extends Component<Props, State> {
  private retryCount = 0;
  private maxRetries = 3;
  /** Remounts after a swallowed translation/extension error, capped to avoid a loop. */
  private recoveryCount = 0;
  private maxRecoveries = 3;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      recoveryKey: 0,
      recoveredFromDomError: false,
      reloadingForUpdate: false,
      chunkLoadFailed: false,
    };
    
    // Set up global error handlers for extension errors
    this.setupGlobalErrorHandlers();
  }

  setupGlobalErrorHandlers = () => {
    // Handle uncaught JavaScript errors
    window.addEventListener('error', (event) => {
      if (isExtensionError(event)) {
        console.warn('🧩 Extension error detected and ignored:', event.error?.message || event.message);
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      if (isExtensionError(event.reason)) {
        console.warn('🧩 Extension promise rejection detected and ignored:', event.reason);
        event.preventDefault();
        return false;
      }

      // A dynamic import that is not awaited by a React.lazy boundary rejects
      // here instead of reaching componentDidCatch - same cause, same cure.
      if (isChunkLoadError(event.reason)) {
        console.warn('📦 Chunk load rejection from a previous build:', event.reason?.message);
        event.preventDefault();
        reloadOnceForNewBuild(event.reason);
        return false;
      }
    });
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    // The boundary must always take ownership of the error here — returning
    // `hasError: false` makes React treat it as unhandled and re-throw it past
    // the boundary. Recoverable errors are cleared again in componentDidCatch,
    // which runs in the same commit, so no crash screen is ever painted.
    if (isExtensionError(error) || isDomReconciliationError(error)) {
      console.warn('🌐 Recoverable extension/translation error in React boundary, ignoring...');
      return { hasError: true, error, errorId: '' };
    }

    // Stale build: show "wird aktualisiert", not the crash screen. The reload
    // is triggered from componentDidCatch, which runs in the same commit.
    if (isChunkLoadError(error)) {
      return { hasError: true, error, errorId: '', reloadingForUpdate: true };
    }
    
    // Generate unique error ID for tracking
    const errorId = `ERR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    return {
      hasError: true,
      error,
      errorId
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Filter out extension-related and browser-translation DOM errors
    if (isExtensionError(error) || isDomReconciliationError(error)) {
      console.warn('🌐 Recoverable extension/translation error detected in componentDidCatch, ignoring...', error.message);

      // Swallowing alone is not enough: React unmounted the subtree that threw,
      // so the children must be remounted under a new key or the user is left
      // staring at a blank page. Capped, because a translator that keeps
      // rewriting the DOM would otherwise remount forever.
      if (this.recoveryCount < this.maxRecoveries) {
        this.recoveryCount++;
        console.warn(`🔄 Remounting after recoverable error (${this.recoveryCount}/${this.maxRecoveries})`);
        this.setState((prev) => ({
          hasError: false,
          error: null,
          errorInfo: null,
          errorId: '',
          recoveryKey: prev.recoveryKey + 1,
        }));
        return;
      }

      // Out of remounts — show the crash screen with the actual cause instead of
      // a white page, so the user knows to turn the translation/extension off.
      console.warn('⚠️ Recoverable error kept repeating — showing the crash screen');
      this.setState({
        hasError: true,
        error,
        errorInfo,
        errorId: `ERR-${Date.now()}-translation`,
        recoveredFromDomError: true,
      });
      return;
    }
    
    // A route chunk from the previous build is gone. Reload once - that pulls
    // the new index.html and the navigation the user asked for goes through.
    if (isChunkLoadError(error)) {
      console.warn('📦 Chunk from a previous build could not be loaded:', error.message);
      if (reloadOnceForNewBuild(error)) {
        this.setState({ hasError: true, error, errorInfo, reloadingForUpdate: true });
        return;
      }
      // Already reloaded and it still fails - the file really is missing, so
      // stop reloading and tell the user what to do. Evict anyway, so that the
      // manual reload the screen asks for starts from a clean cache.
      console.error('📦 Chunk still missing after a reload — showing the update screen');
      void evictCachedChunk(error);
      this.setState({
        hasError: true,
        error,
        errorInfo,
        errorId: `ERR-${Date.now()}-chunk`,
        reloadingForUpdate: false,
        chunkLoadFailed: true,
      });
      return;
    }

    // Log error details
    console.error('🚨 Error Boundary Caught Error:', {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack
      },
      errorInfo: {
        componentStack: errorInfo.componentStack
      },
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      retryCount: this.retryCount
    });

    this.setState({
      error,
      errorInfo
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // TODO: Send error to monitoring service
    // this.sendErrorToService(error, errorInfo);
  }

  private sendErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    // Integration with error monitoring services like Sentry, LogRocket, etc.
    try {
      // Example Sentry integration:
      // Sentry.withScope((scope) => {
      //   scope.setTag('errorBoundary', true);
      //   scope.setContext('errorInfo', errorInfo);
      //   scope.setLevel('error');
      //   Sentry.captureException(error);
      // });
      
      console.log('Error sent to monitoring service');
    } catch (serviceError) {
      console.error('Failed to send error to monitoring service:', serviceError);
    }
  };

  private handleRetry = () => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      console.log(`🔄 Retrying... (${this.retryCount}/${this.maxRetries})`);
      
      // Same reason as the recovery path: without a new key React re-renders the
      // already-unmounted tree and the user gets a blank page instead of the app.
      this.setState((prev) => ({
        hasError: false,
        error: null,
        errorInfo: null,
        errorId: '',
        recoveredFromDomError: false,
        chunkLoadFailed: false,
        reloadingForUpdate: false,
        recoveryKey: prev.recoveryKey + 1,
      }));
    } else {
      console.warn('⚠️ Maximum retry attempts reached');
    }
  };

  private handleReload = () => {
    // On the chunk screen the cached chunk is the problem — replace it before
    // the reload, or the button just reproduces the error.
    if (this.state.chunkLoadFailed) {
      void evictCachedChunk(this.state.error).finally(() => window.location.reload());
      return;
    }
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private copyErrorDetails = () => {
    const errorDetails = {
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      error: {
        name: this.state.error?.name,
        message: this.state.error?.message,
        stack: this.state.error?.stack
      },
      componentStack: this.state.errorInfo?.componentStack,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2))
      .then(() => {
        console.log('Error details copied to clipboard');
      })
      .catch((err) => {
        console.error('Failed to copy error details:', err);
      });
  };

  render() {
    if (this.state.hasError) {
      // A reload is already on its way — a crash screen would only flash by.
      if (this.state.reloadingForUpdate) {
        return (
          <div
            style={{
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f5f5f5',
            }}
          >
            <Spin size="large" />
            <Text type="secondary">Neue Version wird geladen…</Text>
          </div>
        );
      }

      // Reloading did not help: the chunk is genuinely gone. This is not the
      // page's fault, so do not offer "Try Again" on the same broken build.
      if (this.state.chunkLoadFailed) {
        return (
          <div style={{ padding: '50px', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
            <Result
              status="warning"
              title="Die Anwendung wurde aktualisiert"
              subTitle={
                'Ein Teil der Anwendung konnte nicht geladen werden, weil im Browser noch eine '
                + 'ältere Version geöffnet war. Bitte laden Sie die Seite neu. '
                + 'Hilft das nicht, leeren Sie den Browser-Cache (Strg + F5). '
                + `Fehler-ID: ${this.state.errorId}`
              }
              extra={
                <Space wrap>
                  <Button type="primary" icon={<ReloadOutlined />} onClick={this.handleReload}>
                    Seite neu laden
                  </Button>
                  <Button icon={<HomeOutlined />} onClick={this.handleGoHome}>
                    Zur Startseite
                  </Button>
                </Space>
              }
            />
          </div>
        );
      }

      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div style={{ padding: '50px', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
          <Result
            status="error"
            title={
              this.state.recoveredFromDomError
                ? 'Die Seitenübersetzung stört die Anwendung'
                : 'Something went wrong'
            }
            subTitle={
              this.state.recoveredFromDomError
                ? 'Bitte die automatische Übersetzung des Browsers für diese Seite abschalten (Rechtsklick → „Nie übersetzen") oder die Seite ohne Browser-Erweiterungen öffnen. '
                  + `Fehler-ID: ${this.state.errorId}`
                : `We're sorry, but something unexpected happened. Error ID: ${this.state.errorId}`
            }
            extra={[
              <Space key="actions" direction="vertical" size="middle">
                <Space wrap>
                  {this.retryCount < this.maxRetries && (
                    <Button 
                      type="primary" 
                      icon={<ReloadOutlined />} 
                      onClick={this.handleRetry}
                    >
                      Try Again ({this.maxRetries - this.retryCount} attempts left)
                    </Button>
                  )}
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={this.handleReload}
                  >
                    Reload Page
                  </Button>
                  <Button 
                    icon={<HomeOutlined />} 
                    onClick={this.handleGoHome}
                  >
                    Go Home
                  </Button>
                </Space>
                
                <Collapse ghost>
                  <Panel header="Error Details" key="error-details" extra={<BugOutlined />}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text strong>Error ID:</Text> <Text code>{this.state.errorId}</Text>
                      </div>
                      
                      <div>
                        <Text strong>Error Type:</Text> <Text code>{this.state.error?.name}</Text>
                      </div>
                      
                      <div>
                        <Text strong>Error Message:</Text>
                        <Paragraph>
                          <Text code>{this.state.error?.message}</Text>
                        </Paragraph>
                      </div>
                      
                      {process.env.NODE_ENV === 'development' && (
                        <>
                          <div>
                            <Text strong>Stack Trace:</Text>
                            <Paragraph>
                              <Text code style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                                {this.state.error?.stack}
                              </Text>
                            </Paragraph>
                          </div>
                          
                          <div>
                            <Text strong>Component Stack:</Text>
                            <Paragraph>
                              <Text code style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                                {this.state.errorInfo?.componentStack}
                              </Text>
                            </Paragraph>
                          </div>
                        </>
                      )}
                      
                      <Button onClick={this.copyErrorDetails} size="small">
                        Copy Error Details
                      </Button>
                    </Space>
                  </Panel>
                </Collapse>
              </Space>
            ]}
          />
        </div>
      );
    }

    // The key is what actually remounts the tree after a swallowed error.
    return (
      <React.Fragment key={this.state.recoveryKey}>{this.props.children}</React.Fragment>
    );
  }
}

// HOC for wrapping components with error boundary
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};

export default ErrorBoundary; 